import { z } from "zod";

import { BlacklistImportScope, BlacklistScope } from "@/enum/blacklist.enum";
import { MetaDataSchema } from "@/schemas/meta-data";

export const BlacklistOrganizationInfoSchema = z.object({
	id: z.number(),
	name: z.string().nullable(),
	primaryDomain: z.string().nullable(),
	linkedinUrl: z.string().nullable(),
	logoUrl: z.string().nullable(),
	industry: z.string().nullable(),
});

export const BlacklistProspectInfoSchema = z.object({
	id: z.number(),
	name: z.string().nullable(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	email: z.string().nullable(),
	linkedinUrl: z.string().nullable(),
	title: z.string().nullable(),
	image: z.string().nullable(),
	organization: BlacklistOrganizationInfoSchema.nullable(),
});

export const BlacklistSchema = z.object({
	id: z.number(),
	domain: z.string().nullable(),
	email: z.string().nullable(),
	linkedinUrl: z.string().nullable(),
	linkedinCompanyUrl: z.string().nullable(),
	scope: z.nativeEnum(BlacklistScope).nullable(),
	prospectId: z.number().nullable(),
	organizationId: z.number().nullable(),
	prospect: BlacklistProspectInfoSchema.nullable(),
	organization: BlacklistOrganizationInfoSchema.nullable(),
});

export const CreateBlacklistSchema = z.object({
	domain: z.string().trim().optional(),
	email: z.string().trim().optional(),
	linkedinUrl: z.string().trim().optional(),
	linkedinCompanyUrl: z.string().trim().optional(),
	scope: z.nativeEnum(BlacklistScope).optional(),
	/** The company itself: an entry keyed on it blocks the company with or without a website. */
	organizationId: z.number().optional(),
	isGlobal: z.boolean().optional(),
});

/**
 * The picked CSV itself, not a blob URL: an empty-string default used to pass validation, so
 * submitting without a file silently uploaded the SPA's own HTML instead (PREC-3760).
 */
export const UploadBlacklistSchema = z.object({
	file: z.instanceof(File),
	scope: z.nativeEnum(BlacklistImportScope),
});

export const BlacklistUploadRowErrorSchema = z.object({
	row: z.coerce.number(),
	reason: z.string(),
});

export const BlacklistUploadResultSchema = z.object({
	totalRows: z.coerce.number(),
	imported: z.coerce.number(),
	importedPersons: z.coerce.number(),
	importedOrganizations: z.coerce.number(),
	duplicates: z.coerce.number(),
	skipped: z.coerce.number(),
	recognizedColumns: z.array(z.string()),
	unknownColumns: z.array(z.string()),
	errors: z.array(BlacklistUploadRowErrorSchema),
});

export const BlacklistResponseSchema = z.object({
	data: z.array(BlacklistSchema),
	meta: MetaDataSchema,
});

export type Blacklist = z.infer<typeof BlacklistSchema>;
export type BlacklistOrganizationInfo = z.infer<typeof BlacklistOrganizationInfoSchema>;
export type BlacklistProspectInfo = z.infer<typeof BlacklistProspectInfoSchema>;

export type CreateBlacklist = z.infer<typeof CreateBlacklistSchema>;
export type UploadBlacklist = z.infer<typeof UploadBlacklistSchema>;
export type BlacklistUploadResult = z.infer<typeof BlacklistUploadResultSchema>;
export type BlacklistUploadRowError = z.infer<typeof BlacklistUploadRowErrorSchema>;

export type BlacklistResponse = z.infer<typeof BlacklistResponseSchema>;
